import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box, Typography, Paper, LinearProgress, Skeleton, Table,
  TableHead, TableRow, TableCell, TableBody, Chip, Divider,
  ToggleButton, ToggleButtonGroup, Tooltip, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Switch,
  FormControlLabel, Alert, Stack,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import {
  getBilling,
  getInferenceBalance,
  fundInferenceBalance,
  updateInferenceAutoRecharge,
} from '../../api/developerApi';
import ApiErrorAlert from '../../components/ApiErrorAlert';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(n) { return (n ?? 0).toLocaleString(); }

/**
 * Render milli-credits as credits without losing the sub-credit charges that
 * ARE the normal case for script execution (1 credit per 100 seconds). 15
 * milli-credits renders "0.015", not "0". Trailing zeros are trimmed so a
 * value that is whole in credits still reads as a whole number.
 */
function fmtMilliCredits(milli) {
  const n = milli ?? 0;
  if (n % 1000 === 0) return fmt(n / 1000);
  // Group the integer part the same way the whole-credit branch does — without
  // this, 1,000,000 milli renders "1,000" and 1,000,500 renders "1000.5" on the
  // same row. The fractional part is always exactly 3 digits before trimming,
  // so it is split off rather than passed through toLocaleString.
  const [whole, frac] = (n / 1000).toFixed(3).split('.');
  // `\.?` so an all-zero fraction cannot leave a bare trailing dot. Unreachable
  // while creditsMilli is an integer (the branch above catches it), but the
  // regex should not depend on that to be safe.
  return `${fmt(Number(whole))}.${frac}`.replace(/\.?0+$/, '');
}
function fmtBytes(n) {
  if (!n) return '0 B';
  if (n >= 1e9) return (n / 1e9).toFixed(2) + ' GB';
  if (n >= 1e6) return (n / 1e6).toFixed(2) + ' MB';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + ' KB';
  return n + ' B';
}

function SectionHeader({ title, tooltip }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
      <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
      {tooltip && (
        <Tooltip title={tooltip} placement="right">
          <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
      )}
    </Box>
  );
}

function StatRow({ label, count, bytes, credits, estimatedCredits, loading, tooltip }) {
  if (loading) return (
    <TableRow>
      <TableCell><Skeleton /></TableCell>
      <TableCell><Skeleton /></TableCell>
      <TableCell><Skeleton /></TableCell>
    </TableRow>
  );
  const creditVal = credits ?? estimatedCredits;
  const isEstimate = estimatedCredits !== undefined;
  return (
    <TableRow hover>
      <TableCell sx={{ fontSize: 13 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {label}
          {tooltip && (
            <Tooltip title={tooltip} placement="right">
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>
        {bytes !== undefined ? fmtBytes(bytes) : fmt(count)}
      </TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>
        {creditVal > 0
          ? <Chip
              label={isEstimate ? `~${fmt(creditVal)} est.` : fmt(creditVal)}
              size="small"
              sx={{ bgcolor: '#EFF6FF', color: '#1D4ED8', fontWeight: 600, fontSize: 11 }}
            />
          : <Typography variant="caption" color="text.disabled">—</Typography>}
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// UsagePage
// ---------------------------------------------------------------------------

export default function UsagePage() {
  const [view, setView] = useState('total'); // 'total' | 'live' | 'test'

  const { data: billing, isLoading, error: billingError } = useQuery('billing', getBilling);

  const usage = billing?.usage;
  const period = usage?.period ?? '—';

  // Select the data slice based on view toggle
  const data = view === 'live'
    ? usage?.tenants?.live
    : view === 'test'
    ? usage?.tenants?.test
    : usage;

  const creditsUsed = data?.credits?.used ?? 0;
  const planLimit = billing?.planLimitCredits ?? null;
  const pct = planLimit ? Math.min(100, Math.round((creditsUsed / planLimit) * 100)) : 0;

  const breakdown = usage?.credits?.breakdown ?? {};

  const sq = data?.search?.queries ?? {};
  const si = data?.search?.ingest ?? {};
  const ss = data?.search?.indexStorageBytes ?? {};
  const di = data?.documents?.ingest ?? {};
  const ds = data?.documents?.secondaryStorageBytes ?? {};
  const dl = data?.documents?.downloads ?? {};
  const dw = data?.documents?.writes ?? {};
  const dps = data?.documents?.primaryStorageBytes ?? {};
  // Identity (user + entity) writes are tenant-wide, so the report rolls them up
  // at the partner level only — they don't split by live/test env.
  const identity = usage?.identity ?? {};
  // Script execution time (0.43.0) is account-level for the same reason: the
  // report's per-environment `TenantDetail` carries credits/search/documents/
  // records/inference and NO `execution` block (UsageReportResponse.TenantDetail),
  // so this reads off `usage`, never the view-toggled `data` — the same shape
  // `identity` above has. Toggling to Live/Test must not blank it out.
  const execution = usage?.execution ?? {};
  const rw = data?.records?.writes ?? {};
  const rs = data?.records?.primaryStorageBytes ?? {};
  const rps = data?.records?.secondaryStorageBytes ?? {};
  const ras = data?.records?.auditStorageBytes ?? {};

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Usage</Typography>
          <Typography variant="body2" color="text.secondary">
            Billing period: {isLoading ? <Skeleton component="span" width={60} /> : period}
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, v) => v && setView(v)}
          size="small"
        >
          <ToggleButton value="total">All envs</ToggleButton>
          <ToggleButton value="live">Live</ToggleButton>
          <ToggleButton value="test">Test</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ApiErrorAlert error={billingError} sx={{ mb: 2 }} />

      {/* Credits summary */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>Credits used</Typography>
          {isLoading
            ? <Skeleton width={100} />
            : <Typography variant="subtitle2" fontWeight={600}>
                {fmt(creditsUsed)} / {planLimit ? fmt(planLimit) : '∞'}
              </Typography>}
        </Box>
        {isLoading
          ? <Skeleton height={8} sx={{ borderRadius: 1 }} />
          : planLimit
            ? <LinearProgress variant="determinate" value={pct} sx={{
                height: 8, borderRadius: 1, bgcolor: '#DBEAFE',
                '& .MuiLinearProgress-bar': {
                  bgcolor: pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#3B82F6',
                },
              }} />
            : <Typography variant="caption" color="text.secondary">Unlimited</Typography>}
        {!isLoading && planLimit && (
          <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
            {pct}% of plan limit used
          </Typography>
        )}

        {/* Credit breakdown chips — total view only.
            EXHAUSTIVE over the API's CreditBreakdown, not a selection: `credits.used`
            (the headline above) is the sum of every category, so a metered category
            missing here makes the headline fail to reconcile against the chips beside
            it. `scriptExecution` is #1139's trigger execution-time charge; `identity`,
            `reads` and `dataOut` had drifted out of this list the same way and were
            restored alongside it (#1145). A chip is hidden at 0, so adding a category
            costs nothing on an account that never uses it. */}
        {view === 'total' && !isLoading && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {[
              { label: 'Search queries', value: breakdown.searchQueries },
              { label: 'Search ingest', value: breakdown.searchIngest },
              { label: 'Documents', value: breakdown.documents },
              { label: 'Records', value: breakdown.records },
              { label: 'Identity', value: breakdown.identity },
              { label: 'Storage (est.)', value: breakdown.storageEstimate },
              { label: 'Read overage', value: breakdown.reads },
              { label: 'Data out overage', value: breakdown.dataOut },
              { label: 'Script execution', value: breakdown.scriptExecution },
            ].map(({ label, value }) => value > 0 && (
              <Chip
                key={label}
                label={`${label}: ${fmt(value)}`}
                size="small"
                sx={{ bgcolor: '#F4F4F5', color: '#3F3F46', fontSize: 11 }}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Search */}
      <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2, mb: 3 }}>
        <Box sx={{ p: 2.5, pb: 1 }}>
          <SectionHeader title="Search" />
        </Box>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Operation</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Volume</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Credits</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <StatRow label="Text queries"     count={sq.text?.count}     credits={sq.text?.credits}     loading={isLoading} />
            <StatRow label="Semantic queries" count={sq.semantic?.count} credits={sq.semantic?.credits} loading={isLoading} />
            <StatRow label="Hybrid queries"   count={sq.hybrid?.count}   credits={sq.hybrid?.credits}   loading={isLoading} />
            <StatRow label="Text ingest"      bytes={si.text?.bytes}     credits={si.text?.credits}     loading={isLoading} />
            <StatRow label="Semantic ingest"  bytes={si.semantic?.bytes} credits={si.semantic?.credits} loading={isLoading} />
            <StatRow label="Hybrid ingest"    bytes={si.hybrid?.bytes}   credits={si.hybrid?.credits}   loading={isLoading} />
            <StatRow label="Parent chunk ingest" count={si.parentChunks?.count} bytes={si.parentChunks?.bytes} credits={si.parentChunks?.credits} loading={isLoading} />
          </TableBody>
        </Table>
        <Divider />
        <Box sx={{ p: 2.5, pt: 1.5 }}>
          <SectionHeader title="Search storage" tooltip="Estimated monthly charge based on current stored bytes. Billed at end of billing cycle." />
        </Box>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Index type</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Stored</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Est. credits</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <StatRow label="Text index"      bytes={ss.text?.bytes}         estimatedCredits={ss.text?.estimatedCredits}         loading={isLoading} />
            <StatRow label="Semantic index"  bytes={ss.semantic?.bytes}     estimatedCredits={ss.semantic?.estimatedCredits}     loading={isLoading} />
            <StatRow label="Hybrid index"    bytes={ss.hybrid?.bytes}       estimatedCredits={ss.hybrid?.estimatedCredits}       loading={isLoading} />
            <StatRow label="Parent chunks"   bytes={ss.parentChunks?.bytes} estimatedCredits={ss.parentChunks?.estimatedCredits} loading={isLoading} />
          </TableBody>
        </Table>
      </Paper>

      {/* Documents */}
      <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2, mb: 3 }}>
        <Box sx={{ p: 2.5, pb: 1 }}>
          <SectionHeader
            title="Documents"
            tooltip="Ingest is the one-time fee to add a document (1 credit / 50 docs). Writes are the per-write fee charged when a document is created or its metadata updated — a base plus a charge for each index maintained, the same billable work a record write does. Each equality index (ownership link, external ID, or equality lookup field) adds +1 credit / 1,000 writes; a range-enabled lookup field adds +3."
          />
        </Box>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Operation</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Volume</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Credits</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <StatRow label="Text ingest"   count={di.text?.count} credits={di.text?.credits} loading={isLoading} />
            <StatRow label="File ingest"   count={di.file?.count} credits={di.file?.credits} loading={isLoading} />
            <StatRow label="Writes"              count={dw.count}            credits={dw.credits} loading={isLoading} />
            <StatRow label="Index writes" count={dw.indexCount} credits={null}       loading={isLoading} />
            <StatRow label="Downloads"     bytes={dl.bytes}       credits={dl.credits}       loading={isLoading} />
            <StatRow label="Primary storage (DDB)" bytes={dps.bytes}      estimatedCredits={dps.estimatedCredits}      loading={isLoading} />
            <StatRow label="Text storage"  bytes={ds.text?.bytes} estimatedCredits={ds.text?.estimatedCredits} loading={isLoading} />
            <StatRow label="File storage"  bytes={ds.file?.bytes} estimatedCredits={ds.file?.estimatedCredits} loading={isLoading} />
          </TableBody>
        </Table>
      </Paper>

      {/* Records */}
      <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2 }}>
        <Box sx={{ p: 2.5, pb: 1 }}>
          <SectionHeader title="Records" />
        </Box>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Operation</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Volume</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Credits</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <StatRow label="Writes"              count={rw.count}            credits={rw.credits}                    loading={isLoading} />
            <StatRow label="Index writes" count={rw.indexCount} credits={null}                          loading={isLoading} />
            <StatRow label="Storage"             bytes={rs.bytes}            estimatedCredits={rs.estimatedCredits}  loading={isLoading}
              tooltip="Primary record storage (the structured row data). Billed per period at a higher per-MB rate than externalized payloads — see the pricing page for current rates." />
            <StatRow label="Payload storage (S3)" bytes={rps.bytes}          estimatedCredits={rps.estimatedCredits} loading={isLoading}
              tooltip="Large record payloads externalized to object storage. Billed per period at a lower per-MB rate than primary storage — see the pricing page for current rates." />
            <StatRow label="Audit history storage" bytes={ras.bytes}         estimatedCredits={ras.estimatedCredits} loading={isLoading} />
          </TableBody>
        </Table>
      </Paper>

      {/* Identity (users + entities) — same schema-driven write fee + DDB storage as
           records (#121). Tenant-wide, so the report rolls these up at the partner
           level only; shown across all environments regardless of the view toggle. */}
      <IdentitySection identity={identity} loading={isLoading} />

      {/* Script execution time (0.43.0) — the exact figure behind the whole-credit
           "Script execution" chip above, which rounds to zero (and so hides) for
           almost every account. Account-level, like Identity. */}
      <ExecutionSection execution={execution} loading={isLoading} />

      {/* Inference balance — split-billing pre-paid ledger. Distinct from the
           monthly plan credit allowance shown in the Credits card above. */}
      <InferenceBalanceCard usage={usage} />
    </Box>
  );
}

// ===========================================================================
// IdentitySection
//
// Per-surface (users / identity entities) write fee + DDB storage. Identity
// models run the same schema-driven lookup machinery as records, so they carry
// the same per-write fee and storage rate (#121). Tenant-wide → partner-level
// rollup only. Rendered only when there is identity activity to show.
//
// ADR-0064 (#624): the report's `orgs`/`clients` lines folded into the single
// `entities` line — identity entities in EVERY namespace (the reserved
// org/client included) meter under the one `entity` surface. Reading the
// retired keys here left both rows undefined, which also drove `hasActivity`
// to false and hid the whole section for an entity-only tenant.
// ===========================================================================
function IdentitySection({ identity, loading }) {
  const surfaces = [
    { label: 'Users',    data: identity?.users },
    { label: 'Entities', data: identity?.entities },
  ];
  const hasActivity = surfaces.some(
    (s) => (s.data?.writes?.count ?? 0) > 0 || (s.data?.primaryStorageBytes?.bytes ?? 0) > 0,
  );
  if (!loading && !hasActivity) return null;

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2, mt: 3 }}>
      <Box sx={{ p: 2.5, pb: 1 }}>
        <SectionHeader
          title="Identity"
          tooltip="Users and identity entities run the same schema-driven lookup machinery as records, so they carry the same per-write fee — a 5-credit / 1,000-writes base plus +1 credit / 1,000 writes per equality index and +3 per range index — and the same DDB storage rate (5 credits / 100 MB). Entities cover every namespace, including the built-in org and client. Identity is tenant-wide, so it's rolled up across all environments."
        />
      </Box>
      <Table size="small">
        <TableHead sx={{ bgcolor: 'background.default' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Surface</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Volume</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Credits</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {surfaces.map(({ label, data }) => (
            <React.Fragment key={label}>
              <StatRow label={`${label} — writes`} count={data?.writes?.count} credits={data?.writes?.credits} loading={loading} />
              <StatRow label={`${label} — storage`} bytes={data?.primaryStorageBytes?.bytes} estimatedCredits={data?.primaryStorageBytes?.estimatedCredits} loading={loading} />
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

// ===========================================================================
// ExecutionSection
//
// Script execution time and its charge (0.43.0 `UsageReportResponse.execution`).
//
// Its relationship to the "Script execution" chip in the Credits card above is
// the reason this section exists rather than a duplicate of it. That chip
// renders `credits.breakdown.scriptExecution`, which is WHOLE credits, and hides
// at zero — correctly, because the chips must sum to `credits.used`, which is
// also whole. But at 1 credit per 100 SECONDS, script execution rounds to zero
// whole credits for essentially every real account, so for the tenant this
// section serves the chip is ABSENT, not merely small. That is arithmetically
// right for the chip and useless to the developer, and it is why the exact
// figure lives here instead.
//
// (Deliberately NOT fixed by rendering `scriptExecutionMilli` in the chip, which
// a review suggested: the chip row's documented invariant is that the categories
// sum to the whole-credit headline beside them, and a milli-credit chip in that
// row would break the reconciliation it exists to make checkable.)
//
// This section is the millisecond detail the charge is derived from, and it
// exists because the charge's whole defence is that it is CHECKABLE by the
// developer without reconstructing anything:
//
//     billableMillis = totalMillis + (200 * executions) - includedMillis
//
// So the four figures are rendered in that order, on one surface. Dropping any
// one of them turns a checkable number into a number you have to trust.
//
// THE CHARGE IS RENDERED FROM `creditsMilli`, NOT `credits`. `credits` is
// rounded DOWN to whole credits and, at 1 credit per 100 SECONDS of execution,
// is 0 for essentially every real account (the API's own example: 1,500 billable
// ms = 0 credits = 15 milli-credits). Rendering `credits` through the shared
// StatRow — which shows an em-dash for any non-positive value — would print a
// dash beside a tenant that is genuinely being billed. That is the one number on
// this page a developer would dispute, so it is shown at the precision the
// platform actually charges at.
//
// Account-level: `TenantDetail` has no `execution` block, so this does not vary
// with the Live/Test view toggle (same as Identity above).
// ===========================================================================
export function ExecutionSection({ execution, loading }) {
  const executions = execution?.executions ?? 0;
  // Hidden entirely for an account that has never run a script — every other
  // figure is meaningless without at least one execution, and the great majority
  // of accounts will never have one. Matches IdentitySection's hasActivity gate.
  if (!loading && executions <= 0) return null;

  const creditsMilli = execution?.creditsMilli ?? 0;

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2, mt: 3 }}>
      <Box sx={{ p: 2.5, pb: 1 }}>
        <SectionHeader
          title="Script execution"
          tooltip="Wall-clock time your trigger rules and POST /v1/scripts/execute calls spent running, and the surcharge on it. Each billable operation your script performs includes execution time at no charge (350 ms for a simple record write, 1,000 ms for a document ingest, 150 ms for a read charged as overage — a read inside your plan's allowance earns nothing), and every execution carries a 200 ms platform baseline. You are billed only for time beyond what your operations included, at 1 credit per 100 seconds — so a script that does real work and returns promptly pays nothing. Firings refused before your script runs are never charged and never counted here. Account-level, across all environments."
        />
      </Box>
      <Table size="small">
        <TableHead sx={{ bgcolor: 'background.default' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Measure</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <MillisRow label="Executions" value={executions} unit="" loading={loading} />
          <MillisRow
            label="Total time"
            value={execution?.totalMillis}
            loading={loading}
            tooltip="Measured wall-clock time across every metered execution this period."
          />
          <MillisRow
            label="Included at no charge"
            value={execution?.includedMillis}
            loading={loading}
            tooltip="The included-time allowance actually CONSUMED this period, not the allowance your operations earned — an execution that finished well inside its included time contributes only what it used. It also absorbs the full duration of any execution we do not charge for at all, so it can exceed what your operations earned. Note an indexed, foldered or oversized write earns a different amount than a simple one, because it is priced differently: this figure is the exact total applied, so you do not have to reconstruct it."
          />
          <MillisRow
            label="Billable time"
            value={execution?.billableMillis}
            loading={loading}
            tooltip="Total time, plus a 200 ms baseline per execution, minus the included time above. This is what the charge below is computed from."
          />
          <TableRow hover>
            <TableCell sx={{ fontSize: 13 }}>Charge</TableCell>
            <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>
              {loading
                ? <Skeleton />
                : creditsMilli > 0
                  ? <Chip
                      label={`${fmtMilliCredits(creditsMilli)} credits`}
                      size="small"
                      sx={{ bgcolor: '#EFF6FF', color: '#1D4ED8', fontWeight: 600, fontSize: 11 }}
                    />
                  : <Typography variant="caption" color="text.disabled">&mdash;</Typography>}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Paper>
  );
}

/** One millisecond (or bare count) row in the execution table. */
function MillisRow({ label, value, unit = ' ms', loading, tooltip }) {
  if (loading) return (
    <TableRow>
      <TableCell><Skeleton /></TableCell>
      <TableCell><Skeleton /></TableCell>
    </TableRow>
  );
  return (
    <TableRow hover>
      <TableCell sx={{ fontSize: 13 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {label}
          {tooltip && (
            <Tooltip title={tooltip} placement="right">
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>
        {`${fmt(value)}${unit}`}
      </TableCell>
    </TableRow>
  );
}

// ===========================================================================
// InferenceBalanceCard
//
// Shows the partner's pre-paid inferenceBalanceCents balance plus the
// per-endpoint inference activity from the usage report. Two actions:
//   1. "Fund" — opens a dialog to top up via Stripe Checkout (paid plan only)
//   2. "Auto-recharge" — opens a settings dialog to enable/configure
//
// Both actions are gated on `canFund` returned by GET /developer/billing/inference
// (paid plan + Stripe customer on file). Free-tier partners start at $0.00 (the
// unconditional 100¢ provision seed was removed, #319) and get a disabled
// "Upgrade to fund" CTA — they fund inference only after upgrading to a paid plan.
// ===========================================================================
function InferenceBalanceCard({ usage }) {
  const queryClient = useQueryClient();
  const { data: inf, isLoading, error: balanceError } = useQuery('inferenceBalance', getInferenceBalance);

  const [fundOpen, setFundOpen]       = useState(false);
  const [autoOpen, setAutoOpen]       = useState(false);

  const inference = usage?.inference;
  const endpoints = inference?.endpoints ?? {};
  const totals    = inference?.totals    ?? {};

  const balanceCents = inf?.balanceCents ?? 0;
  const auto         = inf?.autoRecharge ?? {};
  const canFund      = inf?.canFund ?? false;
  const plan         = inf?.plan ?? 'free';

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2, mt: 3 }}>
      {balanceError && (
        <Box sx={{ p: 2.5, pb: 0 }}>
          <ApiErrorAlert error={balanceError} />
        </Box>
      )}
      <Box sx={{ p: 2.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <SectionHeader title="Inference balance" tooltip="Pre-paid ledger that absorbs the token-cost portion of inference calls. Separate from your monthly plan allowance, which covers the per-call platform overhead." />
        </Box>
        <Stack direction="row" spacing={1}>
          {canFund
            ? <Button size="small" variant="outlined" onClick={() => setFundOpen(true)}>Add funds</Button>
            : <Tooltip title={plan === 'free' ? 'Upgrade to Starter to fund manually' : 'Set up a payment method to fund manually'}>
                <span><Button size="small" variant="outlined" disabled>Add funds</Button></span>
              </Tooltip>}
          {canFund
            ? <Button size="small" variant="outlined" onClick={() => setAutoOpen(true)}>
                Auto-recharge {auto.enabled ? '(on)' : ''}
              </Button>
            : <Tooltip title="Auto-recharge requires a paid plan and saved payment method">
                <span><Button size="small" variant="outlined" disabled>Auto-recharge</Button></span>
              </Tooltip>}
        </Stack>
      </Box>

      <Box sx={{ px: 2.5, pb: 2 }}>
        {isLoading
          ? <Skeleton width={160} height={40} />
          : <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h4" fontWeight={700}>
                ${(balanceCents / 100).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({balanceCents.toLocaleString()}¢ available)
              </Typography>
              {auto.enabled && (
                <Chip
                  label={`Auto-recharge: $${((auto.amountCents ?? 0) / 100).toFixed(2)} when below $${((auto.thresholdCents ?? 0) / 100).toFixed(2)}`}
                  size="small"
                  sx={{ ml: 1, bgcolor: '#ECFDF5', color: '#065F46', fontWeight: 600, fontSize: 11 }}
                />
              )}
            </Box>}
        {auto.enabled && auto.thresholdCents && auto.amountCents && balanceCents < auto.thresholdCents * 1.25 && (
          <Alert severity="info" sx={{ mt: 1.5, fontSize: 12 }}>
            Your balance is close to the auto-recharge threshold. The next inference call may trigger a charge of ${((auto.amountCents) / 100).toFixed(2)}.
          </Alert>
        )}
        {!isLoading && !canFund && plan === 'free' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            This is a one-time free-tier starting balance — it isn&apos;t topped up automatically and
            can&apos;t be funded on the free plan. Upgrade to Starter to add funds or enable auto-recharge.
          </Typography>
        )}
      </Box>

      {/* Per-endpoint inference activity for the current month */}
      <Divider />
      <Table size="small">
        <TableHead sx={{ bgcolor: 'background.default' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Endpoint</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Calls</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Input tokens</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Output tokens</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <InferenceRow label="POST /v1/chat"               data={endpoints.chat} loading={isLoading} />
          <InferenceRow label="POST /v1/rag"                data={endpoints.rag}  loading={isLoading} />
          <InferenceRow label="POST /v1/documents/{id}/ask" data={endpoints.ask}  loading={isLoading} />
          <TableRow sx={{ '& td': { fontWeight: 600, bgcolor: '#FAFAFA' } }}>
            <TableCell sx={{ fontSize: 13 }}>Total</TableCell>
            <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{fmt(totals.calls)}</TableCell>
            <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{fmt(totals.inputTokens)}</TableCell>
            <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{fmt(totals.outputTokens)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Box sx={{ px: 2.5, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Just created a key? New keys take up to ~5 minutes to start authorizing requests
          (the authorizer&apos;s cache window). A 403 in that window usually means the key is still
          propagating, not that it&apos;s misconfigured.
        </Typography>
      </Box>

      <FundDialog
        open={fundOpen}
        onClose={() => setFundOpen(false)}
      />
      <AutoRechargeDialog
        open={autoOpen}
        onClose={() => setAutoOpen(false)}
        current={auto}
        onSaved={() => queryClient.invalidateQueries('inferenceBalance')}
      />
    </Paper>
  );
}

function InferenceRow({ label, data, loading }) {
  if (loading) {
    return (
      <TableRow>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
      </TableRow>
    );
  }
  const d = data ?? {};
  return (
    <TableRow hover>
      <TableCell sx={{ fontSize: 13, fontFamily: 'monospace' }}>{label}</TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{fmt(d.calls)}</TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{fmt(d.inputTokens)}</TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{fmt(d.outputTokens)}</TableCell>
    </TableRow>
  );
}

function FundDialog({ open, onClose }) {
  const [amount, setAmount] = useState('25.00'); // dollars
  const [error, setError]   = useState('');

  const fund = useMutation(
    (amountCents) => fundInferenceBalance(amountCents),
    {
      onSuccess: (data) => { if (data?.url) window.location.href = data.url; },
      onError:   (e)    => { setError(e?.response?.data?.message || 'Failed to start checkout'); },
    }
  );

  const handleFund = () => {
    setError('');
    const dollars = parseFloat(amount);
    if (Number.isNaN(dollars) || dollars < 1 || dollars > 10000) {
      setError('Amount must be between $1 and $10,000');
      return;
    }
    fund.mutate(Math.round(dollars * 100));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add funds to inference balance</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          You'll be redirected to Stripe Checkout to complete payment. The amount is added to your inference balance immediately on success.
        </Typography>
        <TextField
          label="Amount (USD)"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          fullWidth
          autoFocus
          inputProps={{ inputMode: 'decimal' }}
          helperText="Minimum $1.00, maximum $10,000.00"
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleFund} variant="contained" disabled={fund.isLoading}>
          {fund.isLoading ? 'Redirecting…' : 'Continue to Stripe'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AutoRechargeDialog({ open, onClose, current, onSaved }) {
  const [enabled, setEnabled]     = useState(false);
  const [amount, setAmount]       = useState('50.00');
  const [threshold, setThreshold] = useState('10.00');
  const [error, setError]         = useState('');

  React.useEffect(() => {
    if (open) {
      setEnabled(Boolean(current?.enabled));
      if (current?.amountCents)    setAmount((current.amountCents / 100).toFixed(2));
      if (current?.thresholdCents) setThreshold((current.thresholdCents / 100).toFixed(2));
      setError('');
    }
  }, [open, current]);

  const save = useMutation(
    (body) => updateInferenceAutoRecharge(body),
    {
      onSuccess: () => { onSaved && onSaved(); onClose(); },
      onError:   (e) => { setError(e?.response?.data?.message || 'Failed to save settings'); },
    }
  );

  const handleSave = () => {
    setError('');
    if (!enabled) { save.mutate({ enabled: false }); return; }
    const amt = parseFloat(amount);
    const thr = parseFloat(threshold);
    if (Number.isNaN(amt) || amt < 10 || amt > 10000) {
      setError('Amount must be between $10 and $10,000'); return;
    }
    if (Number.isNaN(thr) || thr < 0 || thr >= amt) {
      setError('Threshold must be ≥ $0 and < amount (otherwise auto-recharge would loop)'); return;
    }
    save.mutate({
      enabled: true,
      amountCents:    Math.round(amt * 100),
      thresholdCents: Math.round(thr * 100),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Auto-recharge inference balance</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          When your inference balance drops below the threshold, we'll automatically charge your saved payment method for the configured amount.
        </Typography>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
          label={enabled ? 'Auto-recharge enabled' : 'Auto-recharge disabled'}
        />
        {enabled && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Recharge amount (USD)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              inputProps={{ inputMode: 'decimal' }}
              helperText="How much to add when triggered. Min $10, max $10,000."
            />
            <TextField
              label="Threshold (USD)"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              fullWidth
              inputProps={{ inputMode: 'decimal' }}
              helperText="Trigger when balance drops below this. Must be less than recharge amount."
            />
            {parseFloat(threshold) > 0 && parseFloat(amount) > parseFloat(threshold)
              && parseFloat(threshold) / parseFloat(amount) >= 0.8 && (
              <Alert severity="warning" sx={{ fontSize: 12 }}>
                A threshold of {Math.round(parseFloat(threshold) / parseFloat(amount) * 100)}% of the recharge amount risks frequent charges. Consider lowering the threshold.
              </Alert>
            )}
          </Stack>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={save.isLoading}>
          {save.isLoading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
